import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  BookOpen, 
  FileText, 
  Target, 
  TrendingUp, 
  ShoppingCart, 
  DollarSign, 
  Settings,
  ChevronRight
} from "lucide-react";
import NavBar from "@/components/NavBar";

export default function KnowledgeCentre() {
  const [selectedCase, setSelectedCase] = useState<string>("soc");

  const businessCases = [
    {
      id: "soc",
      title: "Strategic Outline Case (SOC)",
      description: "Initial business case that sets out the strategic context and makes the case for change",
      status: "Foundation Stage"
    },
    {
      id: "obc", 
      title: "Outline Business Case (OBC)",
      description: "Detailed assessment of options and preferred way forward",
      status: "Development Stage"
    },
    {
      id: "fbc",
      title: "Full Business Case (FBC)", 
      description: "Final business case with detailed implementation plans",
      status: "Implementation Stage"
    }
  ];

  const caseElements = [
    {
      title: "Strategic Case",
      icon: Target,
      description: "Makes the case for change and establishes strategic context",
      color: "text-blue-600"
    },
    {
      title: "Economic Case",
      icon: TrendingUp,
      description: "Demonstrates value for money through options appraisal",
      color: "text-green-600"
    },
    {
      title: "Commercial Case",
      icon: ShoppingCart,
      description: "Sets out procurement strategy and contracting arrangements",
      color: "text-purple-600"
    },
    {
      title: "Financial Case",
      icon: DollarSign,
      description: "Confirms funding arrangements and financial implications",
      color: "text-orange-600"
    },
    {
      title: "Management Case",
      icon: Settings,
      description: "Demonstrates project deliverability and governance",
      color: "text-red-600"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Knowledge Centre</h1>
            <p className="text-muted-foreground">
              Comprehensive guidance on HM Treasury's 5 Case Model and business case development processes
            </p>
          </div>

          {/* Overall Goal Section */}
          <Card className="mb-8 bg-gradient-to-r from-primary/10 to-secondary/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-6 w-6" />
                HM Treasury 5 Case Model Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg mb-4">
                The 5 Case Model provides a framework for developing business cases that demonstrate value for money 
                and support effective decision-making across the public sector.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary mb-1">Strategic</div>
                  <div className="text-sm text-muted-foreground">Context & Change</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary mb-1">Economic</div>
                  <div className="text-sm text-muted-foreground">Value for Money</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary mb-1">Commercial</div>
                  <div className="text-sm text-muted-foreground">Procurement</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Business Case Types */}
          <Tabs value={selectedCase} onValueChange={setSelectedCase} className="mb-8">
            <TabsList className="grid w-full grid-cols-3">
              {businessCases.map((businessCase) => (
                <TabsTrigger key={businessCase.id} value={businessCase.id}>
                  {businessCase.title}
                </TabsTrigger>
              ))}
            </TabsList>

            {businessCases.map((businessCase) => (
              <TabsContent key={businessCase.id} value={businessCase.id} className="space-y-6">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>{businessCase.title}</CardTitle>
                        <CardDescription>{businessCase.description}</CardDescription>
                      </div>
                      <Badge variant="outline">{businessCase.status}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {caseElements.map((element) => {
                        const Icon = element.icon;
                        return (
                          <Card key={element.title} className="hover:shadow-md transition-shadow cursor-pointer">
                            <CardHeader className="pb-3">
                              <CardTitle className="flex items-center gap-2 text-base">
                                <Icon className={`h-5 w-5 ${element.color}`} />
                                {element.title}
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-0">
                              <p className="text-sm text-muted-foreground mb-3">
                                {element.description}
                              </p>
                              <Button variant="outline" size="sm" className="w-full">
                                Learn More <ChevronRight className="h-4 w-4 ml-1" />
                              </Button>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>

          {/* Quick Actions */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Templates & Examples
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Access standardized templates and real-world examples for each business case type
                </p>
                <Button className="w-full">
                  Browse Templates
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Assessment Criteria
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Understanding evaluation criteria and success factors for each case element
                </p>
                <Button className="w-full">
                  View Criteria
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}